// 📊 src/services/dataIntegrationService.js
import { enhancedAPI } from './enhancedAPI';
import { buildApiUrl } from '../utils/apiBase';

class DataIntegrationService {
  constructor() {
    this.integrationStatus = {
      crm: {
        connected: false,
        type: null, // 'salesforce', 'hubspot', 'pipedrive', etc.
        lastSync: null,
        recordCount: 0
      },
      csvData: {
        uploaded: false,
        files: [],
        lastUpload: null,
        recordCount: 0
      },
      companyInfo: {
        completed: false,
        data: null
      }
    };
  }

  // Check what data sources are available
  async getIntegrationStatus() {
    try {
      // In production, this would fetch from backend
      const stored = localStorage.getItem('dataIntegrationStatus');
      if (stored) {
        this.integrationStatus = JSON.parse(stored);
      }
      return this.integrationStatus;
    } catch (error) {
      console.error('Error getting integration status:', error);
      return this.integrationStatus;
    }
  }

  // Update integration status
  async updateIntegrationStatus(updates) {
    try {
      this.integrationStatus = { ...this.integrationStatus, ...updates };
      localStorage.setItem('dataIntegrationStatus', JSON.stringify(this.integrationStatus));
      return this.integrationStatus;
    } catch (error) {
      console.error('Error updating integration status:', error);
      throw error;
    }
  }

  // Set company info
  async setCompanyInfo(companyData) {
    try {
      const updates = {
        companyInfo: {
          completed: true,
          data: companyData
        }
      };
      return await this.updateIntegrationStatus(updates);
    } catch (error) {
      console.error('Error setting company info:', error);
      throw error;
    }
  }

  // Connect CRM
  async connectCRM(crmType, credentials) {
    try {
      // In production, this would make API call to backend
      const response = await enhancedAPI.connectCRM(crmType, credentials);
      
      const updates = {
        crm: {
          connected: true,
          type: crmType,
          lastSync: new Date().toISOString(),
          recordCount: response.recordCount || 0
        }
      };
      
      return await this.updateIntegrationStatus(updates);
    } catch (error) {
      console.error('Error connecting CRM:', error);
      throw error;
    }
  }

  // Upload CSV data
  async uploadCSVData(files) {
    try {
      // In production, this would upload to backend and process
      const uploadPromises = files.map(file => enhancedAPI.uploadCSV(file));
      const results = await Promise.all(uploadPromises);
      
      const totalRecords = results.reduce((sum, result) => sum + (result.recordCount || 0), 0);
      
      const updates = {
        csvData: {
          uploaded: true,
          files: files.map(file => ({
            name: file.name,
            size: file.size,
            uploadDate: new Date().toISOString()
          })),
          lastUpload: new Date().toISOString(),
          recordCount: totalRecords
        }
      };
      
      return await this.updateIntegrationStatus(updates);
    } catch (error) {
      console.error('Error uploading CSV data:', error);
      throw error;
    }
  }

  // Check if specific data type is available
  hasCustomerData() {
    return this.integrationStatus.crm.connected || this.integrationStatus.csvData.uploaded;
  }

  hasCompanyInfo() {
    return this.integrationStatus.companyInfo.completed;
  }

  // Get available dashboard sections based on data
  getAvailableDashboardSections() {
    const sections = {
      // Always available with company info
      competitorIntelligence: this.hasCompanyInfo(),
      marketAnalysis: this.hasCompanyInfo(),
      industryBenchmarks: this.hasCompanyInfo(),
      pricingIntelligence: this.hasCompanyInfo(),
      
      // Requires customer data
      customerHealth: this.hasCustomerData(),
      revenueAnalytics: this.hasCustomerData(),
      churnPrediction: this.hasCustomerData(),
      salesPerformance: this.hasCustomerData(),
      customerSegmentation: this.hasCustomerData(),
      sentimentAnalysis: this.hasCustomerData(),
      
      // Special cases
      executiveSummary: this.hasCompanyInfo(), // Can show basic summary
      aiInsights: this.hasCompanyInfo() || this.hasCustomerData()
    };

    return sections;
  }

  // Get integration prompts for missing data
  getIntegrationPrompts() {
    const prompts = [];

    if (!this.hasCompanyInfo()) {
      prompts.push({
        type: 'company_info',
        title: 'Complete Company Setup',
        description: 'Tell us about your company to unlock industry insights and competitor analysis',
        action: 'Complete Setup',
        priority: 'high'
      });
    }

    if (!this.hasCustomerData()) {
      prompts.push({
        type: 'crm_integration',
        title: 'Connect Your CRM',
        description: 'Connect Salesforce, HubSpot, or other CRM to analyze customer data',
        action: 'Connect CRM',
        priority: 'medium'
      });

      prompts.push({
        type: 'csv_upload',
        title: 'Upload Customer Data',
        description: 'Upload CSV files with customer, sales, or revenue data',
        action: 'Upload Data',
        priority: 'medium'
      });
    }

    return prompts;
  }

  // Generate dashboard data based on available sources
  async generateDashboardData() {
    const status = await this.getIntegrationStatus();
    const availableSections = this.getAvailableDashboardSections();
    
    const dashboardData = {
      hasData: this.hasCustomerData(),
      hasCompanyInfo: this.hasCompanyInfo(),
      availableSections,
      integrationPrompts: this.getIntegrationPrompts(),
      
      // Company-based data (always available if company info exists)
      companyData: status.companyInfo.completed ? {
        industry: status.companyInfo.data.industry,
        companySize: status.companyInfo.data.companySize,
        businessModel: status.companyInfo.data.businessModel,
        primaryGoals: status.companyInfo.data.primaryGoals,
        competitors: status.companyInfo.data.mainCompetitors,
        // Full company data for AI analysis
        fullCompanyData: status.companyInfo.data
      } : null,
      
      // Customer data (only if CRM/CSV connected)
      customerData: this.hasCustomerData() ? {
        source: status.crm.connected ? 'crm' : 'csv',
        recordCount: status.crm.recordCount + status.csvData.recordCount,
        lastSync: status.crm.lastSync || status.csvData.lastUpload
      } : null
    };

    // Generate real intelligence if company info is available
    if (dashboardData.companyData) {
      try {
        dashboardData.realIntelligence = await this.generateRealIntelligence(
          dashboardData.companyData.fullCompanyData
        );
      } catch (error) {
        console.error('Error generating real intelligence:', error);
        dashboardData.realIntelligence = null;
      }
    }

    return dashboardData;
  }

  // Generate real AI-powered intelligence
  async generateRealIntelligence(companyData) {
    try {
      // Call the backend API for real intelligence
      const response = await fetch(buildApiUrl('/real-intelligence/company-analysis'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token') || 'demo'}`
        },
        body: JSON.stringify({ companyData })
      });

      if (response.ok) {
        const result = await response.json();
        return result.data;
      } else {
        console.warn('Real intelligence API call failed, using fallback');
        return null;
      }
    } catch (error) {
      console.error('Error calling real intelligence API:', error);
      return null;
    }
  }

  // Mock data generators for different scenarios
  generateCompanyBasedInsights(companyData) {
    if (!companyData) return null;

    return {
      competitorAnalysis: this.generateCompetitorAnalysis(companyData),
      marketTrends: this.generateMarketTrends(companyData),
      industryBenchmarks: this.generateIndustryBenchmarks(companyData),
      pricingInsights: this.generatePricingInsights(companyData)
    };
  }

  generateCompetitorAnalysis(companyData) {
    // Generate competitor data based on industry and company info
    const competitors = companyData.competitors ? 
      companyData.competitors.split(',').map(c => c.trim()) : 
      this.getDefaultCompetitors(companyData.industry);

    return {
      totalCompetitors: competitors.length + 3, // Add some discovered ones
      directCompetitors: competitors.length,
      marketPosition: Math.floor(Math.random() * 5) + 1,
      competitorData: competitors.map((name, index) => ({
        name,
        marketShare: Math.random() * 30 + 5,
        position: index + 1,
        threat: ['low', 'medium', 'high'][Math.floor(Math.random() * 3)]
      }))
    };
  }

  generateMarketTrends(companyData) {
    return {
      industryGrowth: `${(Math.random() * 20 + 5).toFixed(1)}%`,
      marketSize: `$${(Math.random() * 100 + 50).toFixed(1)}B`,
      trends: [
        'AI adoption increasing',
        'Remote work normalization',
        'Sustainability focus',
        'Digital transformation'
      ]
    };
  }

  generateIndustryBenchmarks(companyData) {
    return {
      avgRevenue: this.getIndustryAverage(companyData.industry, 'revenue'),
      avgGrowth: this.getIndustryAverage(companyData.industry, 'growth'),
      avgChurn: this.getIndustryAverage(companyData.industry, 'churn')
    };
  }

  generatePricingInsights(companyData) {
    return {
      marketPricing: 'Competitive',
      pricingTrend: 'Increasing',
      recommendations: [
        'Consider value-based pricing',
        'Monitor competitor pricing',
        'Test price elasticity'
      ]
    };
  }

  getDefaultCompetitors(industry) {
    const competitorMap = {
      'Technology': ['TechCorp', 'InnovateTech', 'FutureSoft'],
      'Healthcare': ['HealthPlus', 'MedTech Solutions', 'CareFirst'],
      'Finance': ['FinanceFlow', 'MoneyWise', 'InvestPro'],
      'Retail': ['RetailMax', 'ShopSmart', 'MarketLeader'],
      'SaaS': ['CloudSoft', 'SaaS Solutions', 'TechPlatform']
    };
    
    return competitorMap[industry] || ['Competitor A', 'Competitor B', 'Competitor C'];
  }

  getIndustryAverage(industry, metric) {
    const benchmarks = {
      'Technology': { revenue: '$2.5M', growth: '25%', churn: '8%' },
      'Healthcare': { revenue: '$1.8M', growth: '15%', churn: '12%' },
      'Finance': { revenue: '$3.2M', growth: '18%', churn: '10%' },
      'SaaS': { revenue: '$1.5M', growth: '35%', churn: '6%' }
    };
    
    return benchmarks[industry]?.[metric] || 'N/A';
  }
}

export const dataIntegrationService = new DataIntegrationService();
export default DataIntegrationService;
